import { Button, Card, Divider, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { TransactionParameters } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { paymentReportsInitialFacilityHierarchy } from './shared';
import { reportingPanelStyles } from './styles';
import { TransactionReportFilter, type TransactionReportFilterValue } from './TransactionReportFilter';
import type { ReportFormProps } from './types';

type TransactionDateRangeValue = {
	startDate: Dayjs | null;
	endDate: Dayjs | null;
};

const initialDateRange: TransactionDateRangeValue = {
	startDate: null,
	endDate: null,
};

const initialFilter: TransactionReportFilterValue = {
	type: 'all',
};

const addDate = (parameters: TransactionParameters, key: 'endDate', date: Dayjs | null) => {
	if (date) {
		parameters[key] = date.format('YYYY-MM-DD');
	}
};

const buildFilter = (filter: TransactionReportFilterValue): TransactionParameters['filter'] => {
	switch (filter.type) {
		case 'accountId':
			return {
				type: 'accountId',
				value: Number(filter.value),
			};

		case 'subscriptionName':
			return {
				type: 'subscriptionName',
				value: filter.value.trim(),
			};

		case 'invoiceId':
			return {
				type: 'invoiceId',
				value: filter.value.trim(),
			};

		case 'creditId':
			return {
				type: 'creditId',
				value: filter.value.trim(),
			};

		default:
			return undefined;
	}
};

const buildTransactionParameters = ({
	dateRange,
	filter,
	facilityHierarchy,
}: {
	dateRange: TransactionDateRangeValue;
	filter: TransactionReportFilterValue;
	facilityHierarchy: FacilityHierarchyFilterValue;
}): TransactionParameters => {
	if (!dateRange.startDate) {
		throw new Error('Select a start date.');
	}

	const parameters: TransactionParameters = {
		startDate: dateRange.startDate.format('YYYY-MM-DD'),
	};

	addDate(parameters, 'endDate', dateRange.endDate);

	const entityFilter = buildFilter(filter);

	if (entityFilter) {
		parameters.filter = entityFilter;
	}

	applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

	return parameters;
};

export const TransactionReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<TransactionDateRangeValue>(initialDateRange);
	const [filter, setFilter] = useState<TransactionReportFilterValue>(initialFilter);
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(
		paymentReportsInitialFacilityHierarchy,
	);

	const canSubmit = useMemo(() => Boolean(dateRange.startDate), [dateRange.startDate]);

	const reset = () => {
		setDateRange(initialDateRange);
		setFilter(initialFilter);
		setFacilityHierarchy(paymentReportsInitialFacilityHierarchy);
	};

	const validate = () => {
		if (!dateRange.startDate) {
			onValidationError('Select a start date.');
			return false;
		}

		if (filter.type === 'accountId' && !filter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (filter.type === 'subscriptionName' && !filter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		if (filter.type === 'invoiceId' && !filter.value.trim()) {
			onValidationError('Enter an invoice ID.');
			return false;
		}

		if (filter.type === 'creditId' && !filter.value.trim()) {
			onValidationError('Enter a credit ID.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'transaction',
			parameters: buildTransactionParameters({
				dateRange,
				filter,
				facilityHierarchy,
			}),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={2}>
						<Typography variant='subtitle1' fontWeight={600}>
							Date range
						</Typography>

						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
							<DatePicker
								label='Start date *'
								value={dateRange.startDate}
								onChange={(startDate) => {
									setDateRange({
										startDate,
										endDate: dateRange.endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>

							<DatePicker
								label='End date'
								value={dateRange.endDate}
								onChange={(endDate) => {
									setDateRange({
										startDate: dateRange.startDate,
										endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						</Stack>
					</Stack>
				</LocalizationProvider>

				<Divider />

				<TransactionReportFilter filter={filter} onChange={setFilter} />

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
